import { experimental_createQueryPersister } from '@tanstack/react-query-persist-client';
import { createStore, del, get, keys, set } from 'idb-keyval';

import type { Query, QueryFunction, QueryFunctionContext, QueryKey } from '@tanstack/react-query';
import type { AsyncStorage } from '@tanstack/react-query-persist-client';

const INDEXED_DB_NAME = 'cadstrom-query-cache';
const INDEXED_DB_STORE_NAME = 'query-results';

/**
 * URL search parameter that clears Cadstrom-managed persisted query entries from IndexedDB on startup.
 */
export const CLEAR_APP_CACHE_SEARCH_PARAM = 'clearAppCache';

/**
 * Default lifetime for persisted query entries before they are considered expired.
 */
export const PERSISTENT_QUERY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type QueryPersister = ReturnType<typeof experimental_createQueryPersister<string>>;

/**
 * Named persistence policy for a class of expensive query responses.
 *
 * The prefix scopes IndexedDB keys, while the buster invalidates older persisted
 * entries when the cached response shape or semantics change.
 */
export type PersistentQueryPolicy = {
	name: string;
	prefix: string;
	buster: string;
	maxAge: number;
	persister: QueryPersister;
};

type PersistentQueryPolicyConfig = {
	name: string;
	prefix: string;
	buster: string;
	maxAge?: number;
};

type LocalQueryPersister = <T, TQueryKey extends QueryKey>(
	queryFn: QueryFunction<T, TQueryKey>,
	context: QueryFunctionContext<TQueryKey>,
	query: Query,
) => Promise<T>;

type PersistentQueryOptions = {
	staleTime: number;
	gcTime: number;
	persister: LocalQueryPersister;
};

const noopQueryStorage: AsyncStorage = {
	getItem: async () => undefined,
	setItem: async () => undefined,
	removeItem: async () => undefined,
	entries: async () => [],
};

/**
 * Largest serialized entry that will be persisted, in characters.
 *
 * Comfortably covers a normal datasheet while excluding the outliers — a manufacturer's full
 * catalogue rather than a part's datasheet — whose base64 runs to hundreds of megabytes.
 */
export const MAX_PERSISTED_ENTRY_CHARS = 16 * 1024 * 1024;

/**
 * Wraps a storage adapter so entries above `maxChars` are neither written nor read back.
 *
 * Storing an oversized entry costs a full copy of it to serialize on the way in and another to
 * parse on the way back out, enough to threaten the tab for the largest payloads; the query
 * still works when its entry is skipped, it just refetches instead of being restored. The limit
 * applies to reads as well so an entry written before the limit existed is discarded the first
 * time it is seen rather than being parsed again on every startup.
 *
 * @param storage - The adapter to wrap.
 * @param maxChars - The largest entry to read or write through.
 * @returns An adapter that discards oversized entries and passes everything else through.
 */
export function withEntrySizeLimit(storage: AsyncStorage, maxChars: number = MAX_PERSISTED_ENTRY_CHARS): AsyncStorage {
	/** Whether an entry is within the limit, evicting it from the wrapped storage when it is not. */
	const keepIfWithinLimit = async (key: string, value: string | undefined | null): Promise<boolean> => {
		if (value === undefined || value === null || value.length <= maxChars) {
			return true;
		}

		await storage.removeItem(key);
		return false;
	};

	const readEntries = storage.entries;

	return {
		...storage,

		getItem: async (key) => {
			const value = await storage.getItem(key);
			return (await keepIfWithinLimit(key, value)) ? value : undefined;
		},

		setItem: async (key, value) => {
			// Evicting first clears any entry already held under this key, so a restore cannot
			// serve a stale version of a payload we are declining to persist.
			if (!(await keepIfWithinLimit(key, value))) {
				return;
			}

			return storage.setItem(key, value);
		},

		...(readEntries && {
			entries: async () => {
				const stored = await readEntries();
				const withinLimit: [string, string][] = [];

				for (const [key, value] of stored) {
					if (await keepIfWithinLimit(key, value)) {
						withinLimit.push([key, value]);
					}
				}

				return withinLimit;
			},
		}),
	};
}

/**
 * Creates the IndexedDB-backed storage adapter used by TanStack's per-query persister.
 *
 * The adapter exposes entry iteration so startup garbage collection and logout cleanup
 * can remove only Cadstrom-managed persisted query entries.
 */
export function createIndexedDbQueryStorage(): AsyncStorage {
	const store = createStore(INDEXED_DB_NAME, INDEXED_DB_STORE_NAME);

	return withEntrySizeLimit({
		getItem: (key) => get<string>(key, store),
		setItem: (key, value) => set(key, value, store),
		removeItem: (key) => del(key, store),
		entries: async () => {
			const storedKeys = await keys<IDBValidKey>(store);
			const stringKeys = storedKeys.filter((key): key is string => typeof key === 'string');
			const entries = await Promise.all(
				stringKeys.map(async (key): Promise<[string, string] | null> => {
					const value = await get<string>(key, store);
					return value === undefined ? null : [key, value];
				}),
			);

			return entries.filter((entry): entry is [string, string] => entry !== null);
		},
	});
}

function createDefaultQueryStorage(): AsyncStorage {
	if (typeof window === 'undefined' || typeof window.indexedDB === 'undefined') {
		return noopQueryStorage;
	}

	return createIndexedDbQueryStorage();
}

const persistentQueryStorage = createDefaultQueryStorage();

/**
 * Builds a reusable persistence policy from stable cache naming metadata.
 *
 * Tests can pass a custom storage adapter; production uses the shared IndexedDB
 * adapter when available and a no-op adapter during SSR or unsupported browser contexts.
 */
export function createPersistentQueryPolicy(
	config: PersistentQueryPolicyConfig,
	storage: AsyncStorage = persistentQueryStorage,
): PersistentQueryPolicy {
	const maxAge = config.maxAge ?? PERSISTENT_QUERY_MAX_AGE_MS;

	return {
		...config,
		maxAge,
		persister: experimental_createQueryPersister({
			storage,
			buster: config.buster,
			maxAge,
			prefix: config.prefix,
			refetchOnRestore: false,
		}),
	};
}

/**
 * Persistence policies currently allowed for frontend query opt-in.
 *
 * New persistent query families should be added here instead of constructing
 * ad hoc persisters at call sites.
 */
export const persistentQueryPolicies = {
	projectModel: createPersistentQueryPolicy({
		name: 'project-model',
		prefix: 'cadstrom-project-model-query',
		buster: 'project-model-query-v1',
	}),
	// No query family writes this policy; it exists so startup GC and logout
	// clearing purge entries persisted under its prefix, which its buster
	// marks as stale.
	datasheetFile: createPersistentQueryPolicy({
		name: 'datasheet-file',
		prefix: 'cadstrom-datasheet-file-query',
		buster: 'datasheet-file-query-v2',
	}),
	rendererAssetImage: createPersistentQueryPolicy({
		name: 'renderer-asset-image',
		prefix: 'cadstrom-renderer-asset-image-query',
		buster: 'renderer-asset-image-query-v1',
	}),
} as const;

/**
 * Returns the TanStack query options required to persist one expensive query family.
 *
 * Call sites spread this into TRPC or direct TanStack query options, keeping persistence
 * local to the query declaration while sharing lifetime, buster, and storage behavior.
 */
export function persistentQueryOptions(policy: PersistentQueryPolicy): PersistentQueryOptions;
export function persistentQueryOptions<TOverrides extends object>(
	policy: PersistentQueryPolicy,
	overrides: TOverrides,
): PersistentQueryOptions & TOverrides;
export function persistentQueryOptions(policy: PersistentQueryPolicy, overrides?: object): PersistentQueryOptions {
	return {
		staleTime: Infinity,
		gcTime: policy.maxAge,
		persister: policy.persister.persisterFn as unknown as LocalQueryPersister,
		...overrides,
	};
}

/**
 * Removes persisted query entries whose storage keys match the supplied policy prefixes.
 *
 * This is exported for focused tests and for any future targeted cache-clear flows.
 */
export async function clearPersistentQueryStorage(storage: AsyncStorage, prefixes: readonly string[]) {
	const entries = await storage.entries?.();
	if (!entries) {
		return;
	}

	await Promise.all(
		entries
			.filter(([key]) => prefixes.some((prefix) => key.startsWith(`${prefix}-`)))
			.map(([key]) => storage.removeItem(key)),
	);
}

/**
 * Clears all Cadstrom-managed persisted query families from local IndexedDB storage.
 */
export async function clearPersistentQueryCaches() {
	await clearPersistentQueryStorage(
		persistentQueryStorage,
		Object.values(persistentQueryPolicies).map((policy) => policy.prefix),
	);
}

/**
 * Returns whether a URL query string requests startup clearing of the IndexedDB query cache.
 *
 * The flag is enabled by presence, except explicit `0` or `false` values.
 */
export function shouldClearPersistentQueryCachesFromSearchParams(searchParams: URLSearchParams) {
	const value = searchParams.get(CLEAR_APP_CACHE_SEARCH_PARAM);
	return value !== null && value !== '0' && value.toLowerCase() !== 'false';
}

/**
 * Clears Cadstrom-managed persisted query entries when the startup URL requests it.
 *
 * After a successful clear, the query-string flag is removed with `replaceState`
 * so refreshes do not repeatedly clear the cache.
 */
export async function clearPersistentQueryCachesFromSearchParams() {
	if (typeof window === 'undefined') {
		return false;
	}

	const url = new URL(window.location.href);
	if (!shouldClearPersistentQueryCachesFromSearchParams(url.searchParams)) {
		return false;
	}

	await clearPersistentQueryCaches();
	url.searchParams.delete(CLEAR_APP_CACHE_SEARCH_PARAM);
	window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
	return true;
}

/**
 * Runs policy-scoped garbage collection for expired or busted persisted query entries.
 */
export async function runPersistentQueryCacheGc() {
	await Promise.all(Object.values(persistentQueryPolicies).map((policy) => policy.persister.persisterGc()));
}
